import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ALLOWED_ACTION_PROGRESS_ROUTES } from 'src/constants/action-progress';
import { isRouteAllowed } from 'src/utils/url';

const PanelVisibilityContext = createContext(null);

export const PanelVisibilityProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const [isPageScrollable, setIsPageScrollable] = useState(false);
  const [isScrolledDown, setIsScrolledDown] = useState(false);

  const routeAllowed = useMemo(
    () => isRouteAllowed(pathname, ALLOWED_ACTION_PROGRESS_ROUTES),
    [pathname]
  );

  useEffect(() => {
    const checkScrollable = () => {
      const scrollable = document.body.scrollHeight > window.innerHeight;
      setIsPageScrollable(scrollable);
    };

    const observer = new ResizeObserver(() => {
      checkScrollable();
    });

    observer.observe(document.body);

    checkScrollable();

    window.addEventListener('resize', checkScrollable);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkScrollable);
    };
  }, []);

  return (
    <PanelVisibilityContext.Provider
      value={{
        isPanelVisible,
        setIsPanelVisible,
        routeAllowed,
        isPageScrollable,
        isScrolledDown,
        setIsScrolledDown,
      }}
    >
      {children}
    </PanelVisibilityContext.Provider>
  );
};

PanelVisibilityProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePanelVisibility = () => {
  const context = useContext(PanelVisibilityContext);
  if (!context) {
    throw new Error('usePanelVisibility must be used within a PanelVisibilityProvider');
  }
  return context;
};
